import { AccountStates } from '@generated/graphql';
import type { DraftAccount } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { AccountTypes } from '@onevesthq/ov-enums';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Form } from '@ovComponents/2-component/form/form';
import { BeneficiariesCrud } from '@ovComponents/3-pattern/beneficiaryCrud/beneficiariesCrud';
import {
  GoalLinkingInput,
  getValidGoals,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import type { FormErrors } from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { Box, Button, Grid, Typography, useTheme } from 'hephaestus/web';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeadingWithCheckbox } from '../tfsa/tsfa.dialogContent';

type RRSPDialogContentInterface = {
  userData: any;
  showGoalLinkingSection: boolean;
  onAccountCreated: (account: DraftAccount) => void;
  onDialogClose: () => void;
};

export const RRSPDialogContent = ({
  userData,
  showGoalLinkingSection,
  onAccountCreated,
  onDialogClose,
}: RRSPDialogContentInterface) => {
  const { t } = useTranslation(['workflowCompletions', 'affiliationTypes']);
  const [beneficiariesEnabled, setBeneficiariesEnabled] =
    useState<boolean>(false);
  const [beneficiaries, setBeneficiaries] = useState<Affiliation[]>([]);
  const [goalsLinked, setGoalsLinked] = useState<Goal[]>([]);
  const goals = getValidGoals(userData);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [touched, setTouched] = useState<string[]>([]);
  const theme = useTheme();

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      let newErrors: FormErrors = null;

      if (
        beneficiariesEnabled &&
        (candidateFields?.includes('beneficiaries') ||
          candidateFields === undefined)
      ) {
        const totalAllocation = beneficiaries.reduce(
          (value, prev) => value + (prev.allocation ?? 0),
          0,
        );
        if (totalAllocation !== 100) {
          newErrors = { beneficiaries: { active: true } };
        }
      }

      setErrors(newErrors);
      return newErrors;
    },
    [beneficiariesEnabled, beneficiaries],
  );

  const submit = () => {
    const formErrors = validate();
    if (formErrors) {
      setTouched(Object.keys(formErrors));
      return;
    }

    const affiliations: Affiliation[] = [...beneficiaries];

    onAccountCreated({
      type: AccountTypes.RRSP,
      applyForGovFunds: [],
      id: '',
      state: AccountStates.Active,
      user: userData,
      affiliations,
      goalsLinked,
    });
  };

  useEffect(() => {
    validate(touched);
  }, [validate, touched]);

  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:rrspAccountCreation.title')}
      </Typography>

      <Box
        sx={{
          minHeight: '200px',
          my: 3,
        }}
      >
        <HeadingWithCheckbox
          label={t('workflowCompletions:tfsaAccountCreation.addBeneficiaries')}
          checked={beneficiariesEnabled}
          onChange={setBeneficiariesEnabled}
        />
        <Grid style={beneficiariesEnabled ? {} : { opacity: 0.5 }}>
          {errors?.beneficiaries && (
            <Typography
              variant='bodySmall'
              sx={{ color: theme.color.negative }}
            >
              {t('components:beneficiaryCrud.allocationError')}
            </Typography>
          )}
          <BeneficiariesCrud
            beneficiaries={beneficiaries}
            setBeneficiaries={setBeneficiaries}
            disabled={!beneficiariesEnabled}
          />
        </Grid>
        {showGoalLinkingSection && (
          <Box sx={{ mt: 4 }}>
            <GoalLinkingInput
              goals={goals}
              goalsLinked={goalsLinked}
              setGoalsLinked={setGoalsLinked}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button color='primary' sx={{ mt: 3 }} type='submit'>
          {t('workflowCompletions:rrspAccountCreation.addAccount')}
        </Button>
      </Box>
    </Form>
  );
};

export default RRSPDialogContent;
